import * as Tone from 'tone';
import { openSingingMic } from './audioDevices';

/**
 * The mic (the one chosen in the Record group, see audioDevices.js),
 * captured through an AudioWorklet: every chunk of samples arrives
 * stamped with the audio context frame of its first sample. Voice-call
 * processing (echo cancellation, noise suppression, auto gain) is off: it
 * gates sustained vowels and pumps the level.
 *
 * Graded takes capture on Tone's own context, so the samples line up with
 * what Tone's Transport schedules. The pitch monitor needs no such clock and
 * uses a plain AudioContext of its own, started from the click that opens
 * it, as the scratch recorder does: some browsers only let audio start
 * inside a click, and some mics will not join a context at another sample
 * rate.
 */

const WORKLET_NAME = 'mic-capture';
// Posts contiguous runs of mic samples, each stamped with the context frame
// of its first sample, between 'start' and 'stop'
const WORKLET_SOURCE = `
class MicCapture extends AudioWorkletProcessor {
  constructor() {
    super();
    this._on = false;
    this._chunks = [];
    this._length = 0;
    this._frame = 0;
    this._next = 0;
    this.port.onmessage = (event) => {
      if (event.data === 'start') {
        this._on = true;
      } else if (event.data === 'stop') {
        this._flush();
        this._on = false;
      }
    };
  }

  _flush() {
    if (this._length === 0) return;
    const merged = new Float32Array(this._length);
    let offset = 0;
    for (let i = 0; i < this._chunks.length; i++) {
      merged.set(this._chunks[i], offset);
      offset += this._chunks[i].length;
    }
    this._chunks = [];
    this._length = 0;
    this.port.postMessage({ samples: merged, frame: this._frame }, [merged.buffer]);
  }

  process(inputs) {
    const channel = this._on && inputs[0] && inputs[0][0];
    if (channel && channel.length) {
      if (this._length > 0 && currentFrame !== this._next) this._flush();
      if (this._length === 0) this._frame = currentFrame;
      this._chunks.push(new Float32Array(channel));
      this._length += channel.length;
      this._next = currentFrame + channel.length;
      if (this._length >= 2048) this._flush();
    }
    return true;
  }
}
registerProcessor('${WORKLET_NAME}', MicCapture);
`;

const NODE_OPTIONS = {
  numberOfInputs: 1,
  numberOfOutputs: 1,
  outputChannelCount: [1],
  channelCount: 1,
  channelCountMode: 'explicit'
};

// Plain contexts the worklet has been added to (addModule once per context)
const loaded = new WeakMap();

const withWorkletUrl = async (load) => {
  const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
  try {
    await load(url);
  } finally {
    URL.revokeObjectURL(url);
  }
};


// The browser's voice processing, as a mic track reports it
const PROCESSING = [
  ['echoCancellation', 'echo cancellation'],
  ['noiseSuppression', 'noise suppression'],
  ['autoGainControl', 'auto gain'],
  ['voiceIsolation', 'voice isolation']
];
// What the browser applies to the mic although asked not to: the names of
// what is on, or null if it does not say (echoCancellation can also be a
// mode such as 'all')
const processingOf = (track) => {
  const settings = track?.getSettings?.() || {};
  const reported = PROCESSING.filter(([key]) => typeof settings[key] === 'boolean' || typeof settings[key] === 'string');
  if (reported.length === 0) return null;
  return reported
    .filter(([key]) => settings[key] === true || (typeof settings[key] === 'string' && settings[key] !== 'none'))
    .map(([, name]) => name);
};

/**
 * Opens the mic (asking for permission the first time). Between start()
 * and stop(), onChunk({ samples, frame }) receives the audio; close()
 * releases the mic. `context`: a plain AudioContext to capture on, or
 * omitted for Tone's. Rejects (with the browser's own error) if the mic
 * cannot be opened. `processing` lists any voice processing the browser
 * still applies (null if it does not say).
 */
export async function openMicCapture(onChunk, { context: own = null } = {}) {
  const plain = (ctx) => ({
    context: ctx,
    makeNode: () => new AudioWorkletNode(ctx, WORKLET_NAME, NODE_OPTIONS),
    destination: ctx.destination
  });
  const loadInto = async (ctx) => {
    if (!loaded.has(ctx)) {
      // A failed load is forgotten, so opening again can retry it
      loaded.set(ctx, withWorkletUrl(url => ctx.audioWorklet.addModule(url)).catch((error) => {
        loaded.delete(ctx);
        throw error;
      }));
    }
    await loaded.get(ctx);
  };

  let graph;
  if (own) {
    if (own.state === 'suspended') await own.resume();
    await loadInto(own);
    graph = plain(own);
  } else {
    await Tone.start();
    const tone = Tone.getContext();
    await withWorkletUrl(url => tone.addAudioWorkletModule(url, WORKLET_NAME));
    graph = {
      context: tone,
      makeNode: () => tone.createAudioWorkletNode(WORKLET_NAME, NODE_OPTIONS),
      destination: tone.rawContext.destination
    };
  }

  const stream = await openSingingMic();
  let source;
  let node;
  let mute;
  try {
    try {
      source = graph.context.createMediaStreamSource(stream);
    } catch (error) {
      // Firefox will not connect a mic to a context running at another
      // sample rate. A plain context can be remade at the mic's rate.
      if (!own || error.name !== 'NotSupportedError') throw error;
      const settings = stream.getAudioTracks()[0]?.getSettings?.() || {};
      const Ctx = window.AudioContext || window.webkitAudioContext;
      let lastError = error;
      for (const rate of [settings.sampleRate, 48000, 44100, 16000].filter(Boolean)) {
        try {
          const remade = new Ctx({ sampleRate: rate });
          if (remade.state === 'suspended') remade.resume().catch(() => {});
          await loadInto(remade);
          source = remade.createMediaStreamSource(stream);
          graph = plain(remade);
          break;
        } catch (retry) {
          lastError = retry;
        }
      }
      if (!source) throw lastError;
    }
    const { context, makeNode, destination } = graph;
    node = makeNode();
    // A worklet only runs while it reaches the destination; route it there
    // through a silent gain so the mic never reaches the speakers
    mute = context.createGain();
    mute.gain.value = 0;
    source.connect(node);
    node.connect(mute);
    mute.connect(destination);
  } catch (error) {
    stream.getTracks().forEach(track => track.stop());
    throw error;
  }
  node.port.onmessage = (event) => onChunk(event.data);
  const track = stream.getAudioTracks()[0];
  return {
    // The context actually used (a plain one may have been remade)
    context: graph.context,
    sampleRate: graph.context.sampleRate,
    label: track ? track.label : '',
    processing: processingOf(track),
    start: () => node.port.postMessage('start'),
    stop: () => node.port.postMessage('stop'),
    close: () => {
      node.port.onmessage = null;
      node.port.postMessage('stop');
      try {
        source.disconnect();
        node.disconnect();
        mute.disconnect();
      } catch (e) {
        // Already disconnected
      }
      stream.getTracks().forEach(t => t.stop());
    }
  };
}

/** "NotAllowedError: Permission denied" style text for an error, for display. */
export const describeMicError = (error) => {
  if (!error) return 'unknown error';
  const name = error.name && error.name !== 'Error' ? `${error.name}: ` : '';
  return `${name}${error.message || String(error)}`;
};
